import { useEffect, useRef, useState } from "react";

/**
 * Clientseitiger Spam-Schutz für öffentliche Formulare (Stufe 1).
 *
 * Achtung: Reiner Client-Schutz lässt sich von entschlossenen Bots umgehen,
 * da der Endpunkt direkt aufrufbar ist. Diese Layer erhöhen die Hürde gegen
 * einfache Bots und liefern dem Backend Signale (Honeypot-Wert, Render-Dauer)
 * zur späteren serverseitigen Auswertung. 
 */

// Mindestzeit zwischen Mount und Submit (ms). Schnellere Submits gelten als Bot.
const MIN_FILL_TIME_MS = 3000;
// Sperrzeit nach erfolgreichem Absenden (ms), gegen schnelle Resubmits.
const RATE_LIMIT_MS = 60000;

interface SpamProtectionOptions {
    rateLimitKey: string;
    minFillTimeMs?: number;
    rateLimitMs?: number;
}

interface SpamSignals {
    [key: string]: string;
}

export const useSpamProtection = ({
    rateLimitKey,
    minFillTimeMs = MIN_FILL_TIME_MS,
    rateLimitMs = RATE_LIMIT_MS,
}: SpamProtectionOptions) => {
    const [honeypot, setHoneypot] = useState<string>('');
    const mountedAt = useRef<number>(Date.now());

    useEffect(() => {
        mountedAt.current = Date.now();
    }, []);

    // Honeypot ausgefüllt → mit hoher Wahrscheinlichkeit ein Bot.
    const honeypotTriggered = () => honeypot.trim().length > 0;

    // Formular zu schnell ausgefüllt → wahrscheinlich ein Bot.
    const filledTooFast = () => (Date.now() - mountedAt.current) < minFillTimeMs;

    // Clientseitiges Rate-Limit: noch im Cooldown nach letztem erfolgreichen Absenden?
    const isRateLimited = () => {
        try {
            const last = window.localStorage.getItem(rateLimitKey);
            if (!last) {
                return false;
            }
            return (Date.now() - Number(last)) < rateLimitMs;
        } catch (e) {
            return false;
        }
    };

    const remainingRateLimitSeconds = () => {
        try {
            const last = window.localStorage.getItem(rateLimitKey);
            if (!last) {
                return 0;
            }
            const remaining = rateLimitMs - (Date.now() - Number(last));
            return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
        } catch (e) {
            return 0;
        }
    };

    // Nach erfolgreichem Absenden den Cooldown starten.
    const markSubmitted = () => {
        try {
            window.localStorage.setItem(rateLimitKey, String(Date.now()));
        } catch (e) {
            // localStorage nicht verfügbar – Rate-Limit dann inaktiv.
        }
    };

    // "Stille" Bot-Erkennung (Honeypot/Timing): Submit ignorieren, dem Bot aber
    // keine Rückmeldung geben (Aufrufer zeigt i.d.R. eine Fake-Erfolgsansicht).
    const isLikelyBot = () => honeypotTriggered() || filledTooFast();

    // Signale, die zur serverseitigen Auswertung mitgesendet werden.
    const getSpamSignals = (): SpamSignals => ({
        website: honeypot,
        form_render_time: String(Date.now() - mountedAt.current),
    });

    return {
        honeypot,
        setHoneypot,
        isLikelyBot,
        isRateLimited,
        remainingRateLimitSeconds,
        markSubmitted,
        getSpamSignals,
    };
};
